// src/services/aiWeatherService.js
// --- UPDATE: new module — live weather feed + edge (in-browser) flood-prediction model
// ============================================================================
// 1. Weather: Open-Meteo (free, keyless, CORS-enabled). Normalised into the exact
//    shape WellManagementPanel / WellAiSummaryCard / WellAiStatusCell already read
//    ({ current, next6hRainMm, next24hRainMm, past24hRainMm, peakPrecipProbability,
//    hourly[], daily[] }), so one feed serves the whole page.
// 2. Edge flood model: an explainable logistic model that turns water-level
//    history + rainfall forecast + wellbore clearance into the same prediction
//    fields the server ML pushes over WebSocket (floodRiskProbability,
//    predictedSeverity, estimatedMinutesToOvertopping, …). It is a FALLBACK:
//    server predictions always take precedence while they are fresh.
// ============================================================================
// --- UPDATE (weather v2): the Open-Meteo "current" value is a global-model forecast (hourly, interpolated
// to 15 min for the Bahamas) — it missed the heavy rain over Nassau. The page now asks the FlowSense server
// first (GET /simulations/weather/now): several weather models + staff rain reports, one result for every
// page and for the drainage / road ponding forecasts. The direct Open-Meteo call stays as the fallback.
// --- END UPDATE
import { authenticatedFetch } from './api'; // --- UPDATE (weather v2)

export const DEFAULT_WEATHER_LOCATION = Object.freeze({ lat: 25.0443, lng: -77.3504, label: 'New Providence' });
export const EDGE_MODEL_VERSION = 'edge-hydro-1.0';

const OPEN_METEO_URL = 'https://api.open-meteo.com/v1/forecast';
const weatherCache = new Map();    // key -> { data, ts }
const weatherInflight = new Map(); // key -> Promise

const round1 = (n) => Math.round(n * 10) / 10;
const numOrNull = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// --- UPDATE (weather v2): WMO codes 1 / 2 / 3 and the rain intensities have their own labels
const WEATHER_CODE_LABELS = [
  [0, 'Clear'], [1, 'Mainly clear'], [2, 'Partly cloudy'], [3, 'Overcast'], [48, 'Fog'], [57, 'Drizzle'],
  [61, 'Light rain'], [63, 'Rain'], [65, 'Heavy rain'], [67, 'Freezing rain'], [77, 'Snow'],
  [81, 'Rain showers'], [82, 'Violent rain showers'], [86, 'Snow showers'], [99, 'Thunderstorm'],
];
// --- END UPDATE

export const weatherCodeLabel = (code) => {
  const n = numOrNull(code);
  if (n === null) return '—';
  const hit = WEATHER_CODE_LABELS.find(([max]) => n <= max);
  return hit ? hit[1] : 'Thunderstorm';
};

export const isStormCode = (code) => {
  const n = numOrNull(code);
  return n !== null && n >= 95;
};

/** Open-Meteo JSON → normalised weather object. */
export const normalizeOpenMeteo = (json, nowMs = Date.now()) => {
  const offsetMs = (Number(json?.utc_offset_seconds) || 0) * 1000;
  const toEpoch = (t) => Date.parse(`${t}Z`) - offsetMs;
  const times = json?.hourly?.time || [];
  const precip = json?.hourly?.precipitation || [];
  const probs = json?.hourly?.precipitation_probability || [];

  let startIdx = times.findIndex((t) => toEpoch(t) + 3600000 > nowMs);
  if (startIdx < 0) startIdx = times.length;

  const sumRange = (from, to) => {
    let s = 0;
    for (let i = Math.max(0, from); i < Math.min(precip.length, to); i += 1) s += Number(precip[i]) || 0;
    return round1(s);
  };

  const hourly = [];
  for (let i = startIdx; i < Math.min(times.length, startIdx + 24); i += 1) {
    hourly.push({ time: toEpoch(times[i]), precipMm: Number(precip[i]) || 0, prob: numOrNull(probs[i]) });
  }

  let peakPrecipProbability = null;
  let peakTime = null;
  hourly.forEach((h) => {
    if (h.prob !== null && (peakPrecipProbability === null || h.prob > peakPrecipProbability)) {
      peakPrecipProbability = h.prob;
      peakTime = h.time;
    }
  });

  const today = String(json?.current?.time || '').slice(0, 10);
  const daily = (json?.daily?.time || [])
    .map((d, i) => ({
      date: d,
      rainMm: round1(Number(json.daily.precipitation_sum?.[i]) || 0),
      prob: numOrNull(json.daily.precipitation_probability_max?.[i]),
    }))
    .filter((d) => !today || d.date >= today)
    .slice(0, 3);

  const weatherCode = numOrNull(json?.current?.weather_code);
  return {
    source: 'Open-Meteo',
    fetchedAt: nowMs,
    current: {
      temperatureC: numOrNull(json?.current?.temperature_2m),
      precipitationMm: numOrNull(json?.current?.precipitation),
      windKph: numOrNull(json?.current?.wind_speed_10m),
      weatherCode,
      label: weatherCodeLabel(weatherCode),
    },
    next6hRainMm: sumRange(startIdx, startIdx + 6),
    next24hRainMm: sumRange(startIdx, startIdx + 24),
    past24hRainMm: sumRange(startIdx - 24, startIdx),
    peakPrecipProbability,
    peakTime,
    hourly,
    daily,
  };
};

/**
 * Accepts weather pushed over WebSocket in any of three shapes:
 * already-normalised, raw Open-Meteo, or a flat { rain_6h, rain_24h, … } summary.
 */
export const normalizeIncomingWeather = (raw) => {
  const data = raw?.payload || raw?.data || raw;
  if (!data || typeof data !== 'object') return null;
  if (Number.isFinite(Number(data.next24hRainMm))) {
    return {
      // --- UPDATE (weather v2): keep the server's extras (observation, models, upper6hRainMm, location, warnings)
      ...data,
      fetchedAt: Number.isFinite(Number(data.generatedAt)) ? Number(data.generatedAt) : Date.now(),
      // --- END UPDATE
      source: data.source || 'Server',
      current: data.current || { temperatureC: null, precipitationMm: null, windKph: null, weatherCode: null, label: '—' },
      next6hRainMm: Number(data.next6hRainMm) || 0,
      next24hRainMm: Number(data.next24hRainMm) || 0,
      past24hRainMm: Number(data.past24hRainMm) || 0,
      peakPrecipProbability: numOrNull(data.peakPrecipProbability),
      peakTime: data.peakTime ?? null,
      hourly: Array.isArray(data.hourly) ? data.hourly : [],
      daily: Array.isArray(data.daily) ? data.daily : [],
    };
  }
  if (data.hourly && Array.isArray(data.hourly.time)) {
    return { ...normalizeOpenMeteo(data), source: 'Server (Open-Meteo)' };
  }
  const r6 = numOrNull(data.rain_6h ?? data.rainfall_6h);
  const r24 = numOrNull(data.rain_24h ?? data.rainfall_24h ?? data.precipitation_24h);
  if (r6 === null && r24 === null) return null;
  return {
    source: data.source || 'Server',
    fetchedAt: Date.now(),
    current: {
      temperatureC: numOrNull(data.temperature ?? data.temperature_c),
      precipitationMm: numOrNull(data.precipitation),
      windKph: numOrNull(data.wind_kph ?? data.wind_speed),
      weatherCode: numOrNull(data.weather_code),
      label: weatherCodeLabel(data.weather_code),
    },
    next6hRainMm: r6 ?? 0,
    next24hRainMm: r24 ?? r6 ?? 0,
    past24hRainMm: numOrNull(data.rain_past_24h) ?? 0,
    peakPrecipProbability: numOrNull(data.peak_probability ?? data.precip_probability),
    peakTime: null,
    hourly: [],
    daily: [],
  };
};

const weatherKey = (lat, lng) => `${lat.toFixed(2)},${lng.toFixed(2)}`;

export const getCachedWeather = (lat, lng, maxAgeMs) => {
  const hit = weatherCache.get(weatherKey(lat, lng));
  return hit && Date.now() - hit.ts < maxAgeMs ? hit.data : null;
};

/** Cached + de-duplicated Open-Meteo fetch. */
export const fetchWeather = (lat, lng) => {
  const key = weatherKey(lat, lng);
  if (weatherInflight.has(key)) return weatherInflight.get(key);
  const params = new URLSearchParams({
    latitude: lat.toFixed(4),
    longitude: lng.toFixed(4),
    current: 'temperature_2m,precipitation,weather_code,wind_speed_10m',
    hourly: 'precipitation,precipitation_probability',
    daily: 'precipitation_sum,precipitation_probability_max',
    past_days: '1',
    forecast_days: '3',
    timezone: 'auto',
  });
  const request = fetch(`${OPEN_METEO_URL}?${params.toString()}`)
    .then((res) => {
      if (!res.ok) throw new Error(`Weather service responded ${res.status}`);
      return res.json();
    })
    .then((json) => {
      const data = normalizeOpenMeteo(json);
      weatherCache.set(key, { data, ts: Date.now() });
      return data;
    })
    .finally(() => weatherInflight.delete(key));
  weatherInflight.set(key, request);
  return request;
};

// --- UPDATE (weather v2) -----------------------------------------------------------------------------
const serverUnwrap = (body, what) => {
  if (!body || body.success === false) {
    const err = new Error(body?.message || body?.error || `${what} failed`);
    err.code = body?.code || null;
    err.status = body?.status || null;
    throw err;
  }
  return body.data;
};

/** Rain right now from the FlowSense server (several models + staff reports). Throws when unavailable. */
export const fetchServerWeather = async (lat, lng, { force = false } = {}) => {
  const qs = new URLSearchParams({ lat: String(lat), lng: String(lng), ...(force ? { force: '1' } : {}) });
  const data = serverUnwrap(await authenticatedFetch(`/simulations/weather/now?${qs.toString()}`), 'Weather');
  const normalized = normalizeIncomingWeather(data);
  if (!normalized) throw new Error('Weather: unexpected response');
  return normalized;
};

/** Staff rain report: intensity none | light | moderate | heavy | violent. Resolves with { id, now }. */
export const reportRain = async ({ intensity, latitude, longitude, note } = {}) => {
  const data = serverUnwrap(await authenticatedFetch('/simulations/weather/observations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ intensity, latitude, longitude, note, source: 'web' }),
  }), 'Rain report');
  return { ...data, now: data?.now ? normalizeIncomingWeather(data.now) : null };
};

export const RAIN_INTENSITIES = Object.freeze([
  { key: 'light', label: 'Light', range: '< 2.5 mm/h', colorScheme: 'cyan' },
  { key: 'moderate', label: 'Moderate', range: '2.5–10 mm/h', colorScheme: 'blue' },
  { key: 'heavy', label: 'Heavy', range: '10–50 mm/h', colorScheme: 'purple' },
  { key: 'violent', label: 'Violent', range: '> 50 mm/h', colorScheme: 'red' },
  { key: 'none', label: 'Stopped', range: 'no rain now', colorScheme: 'gray' },
]);
// --- END UPDATE ---------------------------------------------------------------------------------------

/** Mean coordinate of assets that have valid coordinates (falls back to New Providence). */
export const computeFleetCentroid = (assets = []) => {
  let sumLat = 0;
  let sumLng = 0;
  let n = 0;
  (assets || []).forEach((a) => {
    const lat = parseFloat(a?.latitude);
    const lng = parseFloat(a?.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0)) {
      sumLat += lat;
      sumLng += lng;
      n += 1;
    }
  });
  if (!n) return DEFAULT_WEATHER_LOCATION;
  return {
    lat: Math.round((sumLat / n) * 1000) / 1000,
    lng: Math.round((sumLng / n) * 1000) / 1000,
    label: `Asset field (${n} sites)`,
  };
};

// ============================================================================
// EDGE FLOOD MODEL
// ============================================================================

/**
 * Fill-percentage proxy for categorical level readings. HIGH is placed at 90 % (not 100 %)
 * so "HIGH and rising" still has headroom to overtop; numeric levels are used as-is.
 */
const LEVEL_TO_FILL = { EMPTY: 5, LOW: 30, MEDIUM: 60, HIGH: 90, CRITICAL: 98, OVERFLOW: 100, FLOOD: 100 };

export const levelToFill = (level) => {
  if (level === null || level === undefined || level === '') return null;
  const n = Number(level);
  if (Number.isFinite(n)) return clamp(n, 0, 100);
  const v = LEVEL_TO_FILL[String(level).toUpperCase().trim()];
  return v === undefined ? null : v;
};

const toMs = (ts) => {
  if (ts === null || ts === undefined || ts === '') return null;
  const n = typeof ts === 'number' ? ts : Date.parse(ts);
  return Number.isFinite(n) ? (n < 1e12 ? n * 1000 : n) : null;
};

/** Least-squares slope (% per minute) over points within `windowMs` of the newest point. */
export const estimateRiseRate = (points, windowMs = 2 * 60 * 60 * 1000) => {
  const pts = points.filter((p) => p.t !== null && p.fill !== null);
  if (pts.length < 2) return { slope: 0, n: pts.length };
  const newest = Math.max(...pts.map((p) => p.t));
  const recent = pts.filter((p) => newest - p.t <= windowMs);
  if (recent.length < 2) return { slope: 0, n: recent.length };
  const xs = recent.map((p) => (p.t - newest) / 60000);
  const ys = recent.map((p) => p.fill);
  const mx = xs.reduce((a, b) => a + b, 0) / xs.length;
  const my = ys.reduce((a, b) => a + b, 0) / ys.length;
  let num = 0;
  let den = 0;
  xs.forEach((x, i) => {
    num += (x - mx) * (ys[i] - my);
    den += (x - mx) ** 2;
  });
  return { slope: den > 0 ? num / den : 0, n: recent.length };
};

const logistic = (z) => 1 / (1 + Math.exp(-z));

const MODEL = Object.freeze({
  intercept: -4,
  fillWeight: 5,          // × fill fraction (0–1)
  riseWeight: 6,          // × rise rate (% / min), positive only — 0.2 %/min (12 %/h) adds +1.2
  rain24Weight: 0.04,     // × forecast rain next 24h (mm) — 25 mm adds +1.0
  soakWeight: 0.02,       // × observed rain past 24h (mm) — saturated ground
  etaBonus: 1.5,          // added when projected overtopping < 60 min
  stormBonus: 0.5,        // thunderstorm in progress
  rainToFillPerMm: 1.5,   // % fill gained per mm of rain (scaled by silt factor)
});

const severityFor = (p, eta) => {
  if (p >= 0.85 || (eta !== null && eta <= 30)) return 'CRITICAL';
  if (p >= 0.65) return 'HIGH';
  if (p >= 0.4) return 'WARNING';
  return 'NONE';
};

/**
 * Predicts flood risk for one well.
 * @param {Object} params
 * @param {Array}  params.history   newest-first [{ level, timestamp }]
 * @param {Object} params.reading   latest { level, timestamp }
 * @param {Object} params.weather   normalised weather (may be null)
 * @param {number} params.clearanceScore wellbore clearance % (may be null)
 * @returns prediction object compatible with the server ML payload, or null without data
 */
export const computeEdgeFloodPrediction = ({ history = [], reading = null, weather = null, clearanceScore = null, now = Date.now() }) => {
  const points = [];
  if (reading && reading.level !== undefined) points.push({ t: toMs(reading.timestamp), fill: levelToFill(reading.level) });
  (history || []).forEach((h) => points.push({ t: toMs(h.timestamp), fill: levelToFill(h.level) }));
  const valid = points.filter((p) => p.fill !== null);
  if (!valid.length) return null;

  const newestPoint = valid.reduce((a, b) => ((b.t ?? 0) > (a.t ?? 0) ? b : a));
  const fill = newestPoint.fill;
  const ageMin = newestPoint.t ? (now - newestPoint.t) / 60000 : null;
  const { slope, n } = estimateRiseRate(valid);

  const rain6 = Number(weather?.next6hRainMm) || 0;
  const rain24 = Number(weather?.next24hRainMm) || 0;
  const rainPast = Number(weather?.past24hRainMm) || 0;
  const storm = isStormCode(weather?.current?.weatherCode);
  const siltFactor = Number.isFinite(clearanceScore) && clearanceScore < 80 ? 1.3 : 1;

  const rainRate = (MODEL.rainToFillPerMm * siltFactor * rain6) / 360; // % per min over the next 6h
  const effectiveRate = Math.max(slope, 0) + rainRate;
  let eta = null;
  if (fill >= 100) eta = 0;
  else if (effectiveRate > 0.001) {
    const mins = (100 - fill) / effectiveRate;
    eta = mins <= 720 ? Math.round(mins) : null;
  }

  const contributions = {
    'Current water level': MODEL.fillWeight * (fill / 100),
    'Rate of rise': MODEL.riseWeight * Math.max(slope, 0),
    'Forecast rainfall (24h)': MODEL.rain24Weight * rain24,
    'Ground saturation (past 24h rain)': MODEL.soakWeight * rainPast,
    'Projected overtopping < 60 min': eta !== null && eta < 60 ? MODEL.etaBonus : 0,
    'Thunderstorm activity': storm ? MODEL.stormBonus : 0,
  };
  const z = MODEL.intercept + Object.values(contributions).reduce((a, b) => a + b, 0);
  const probability = clamp(logistic(z), 0.01, 0.99); // a model estimate never claims certainty

  // 6-hour probability trajectory (hourly), projecting fill forward
  const forecast = [];
  for (let h = 0; h <= 6; h += 1) {
    const projFill = clamp(fill + effectiveRate * 60 * h, 0, 100);
    const zh = MODEL.intercept + MODEL.fillWeight * (projFill / 100) + contributions['Rate of rise']
      + contributions['Forecast rainfall (24h)'] + contributions['Ground saturation (past 24h rain)'] + contributions['Thunderstorm activity']
      + (projFill >= 100 ? MODEL.etaBonus : 0);
    forecast.push(Math.round(clamp(logistic(zh), 0.01, 0.99) * 1000) / 1000);
  }

  const stale = ageMin !== null && ageMin > 30;
  const confidence = clamp((0.35 + 0.08 * Math.min(n, 6)) * (weather ? 1 : 0.85) * (stale ? 0.6 : 1), 0.2, 0.9);
  const severity = severityFor(probability, eta);

  const recommendations = [];
  if (eta !== null && eta <= 60) recommendations.push('Dispatch a crew — projected overtopping within the hour.');
  if (severity === 'CRITICAL' || severity === 'HIGH') recommendations.push('Alert roads & drainage teams for adjacent corridors.');
  if (rain24 >= 25) recommendations.push(`Pre-position pumps ahead of ${round1(rain24)} mm forecast rainfall.`);
  if (siltFactor > 1) recommendations.push('Schedule desilting — low clearance amplifies rain-driven rise.');
  if (stale) recommendations.push('Latest reading is stale — verify sensor connectivity.');

  return {
    floodRiskProbability: Math.round(probability * 1000) / 1000,
    predictedSeverity: severity,
    estimatedMinutesToOvertopping: eta,
    rateOfRisePerMinute: Math.round(slope * 10000) / 10000,
    confidence: Math.round(confidence * 100) / 100,
    trend: slope > 0.01 ? 'rising' : slope < -0.01 ? 'falling' : 'stable',
    probability_forecast: forecast,
    top_factors: Object.entries(contributions)
      .filter(([, w]) => w > 0.001)
      .map(([feature, weight]) => ({ feature, weight: Math.round(weight * 100) / 100 })),
    recommendations,
    weatherInfluence: weather ? { rain6hMm: rain6, rain24hMm: rain24, pastRainMm: rainPast, storm } : null,
    model_version: EDGE_MODEL_VERSION,
    updated_at: now,
    source: 'edge',
    inputs: { fill, slope, readings: n, ageMin: ageMin !== null ? Math.round(ageMin) : null },
  };
};
// --- END UPDATE